import { WorldPoint } from '../domain/types.ts';

export class Camera {
  public worldX = 0; // meters từ center
  public worldY = 0; // meters từ center
  public zoom = 1.0; // scale factor
  public readonly maxRadiusM = 500; // 500m clamp radius

  private screenWidth = 0;
  private screenHeight = 0;
  public baseZoom = 1.0;
  public minZoom = 0.5;
  public maxZoom = 5.0;

  public resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Tự động căn zoom cơ sở sao cho 500m vừa vặn trên màn hình mobile
    const minDim = Math.min(width, height);
    this.baseZoom = (minDim * 0.8) / (this.maxRadiusM * 2);
    this.minZoom = this.baseZoom * 0.7;
    this.maxZoom = this.baseZoom * 5.0;

    // Nếu chưa zoom hoặc zoom ở mức ban đầu thì đặt theo baseZoom
    if (this.zoom < this.minZoom || this.zoom > this.maxZoom || this.zoom === 1.0) {
      this.zoom = this.baseZoom;
    }
  }

  public zoomBy(factor: number, screenCenterX?: number, screenCenterY?: number): void {
    const prevZoom = this.zoom;
    const targetZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * factor));
    if (Math.abs(targetZoom - prevZoom) < 0.001) return;

    // Zoom hướng về tâm con trỏ hoặc tâm màn hình
    const cx = screenCenterX ?? this.screenWidth / 2;
    const cy = screenCenterY ?? this.screenHeight / 2;

    const worldBefore = this.screenToWorld(cx, cy);
    this.zoom = targetZoom;
    const worldAfter = this.screenToWorld(cx, cy);

    // Bù trừ world position để điểm zoom cố định dưới con trỏ
    this.worldX += worldBefore.x - worldAfter.x;
    this.worldY += worldBefore.y - worldAfter.y;
    this.clampPan();
  }

  public zoomIn(factor = 1.3): void {
    this.zoomBy(factor);
  }

  public zoomOut(factor = 1.3): void {
    this.zoomBy(1 / factor);
  }

  public resetZoom(): void {
    this.zoom = this.baseZoom;
    this.worldX = 0;
    this.worldY = 0;
  }

  private clampPan(): void {
    const dist = Math.hypot(this.worldX, this.worldY);
    if (dist > this.maxRadiusM) {
      const angle = Math.atan2(this.worldY, this.worldX);
      this.worldX = Math.cos(angle) * this.maxRadiusM;
      this.worldY = Math.sin(angle) * this.maxRadiusM;
    }
  }

  public pan(deltaScreenX: number, deltaScreenY: number): void {
    // Convert screen delta sang world delta
    const deltaWorldX = deltaScreenX / this.zoom;
    const deltaWorldY = deltaScreenY / this.zoom;

    this.worldX -= deltaWorldX;
    this.worldY -= deltaWorldY;
    this.clampPan();
  }

  public worldToScreen(worldPoint: WorldPoint): { x: number; y: number } {
    const relX = worldPoint.x - this.worldX;
    const relY = worldPoint.y - this.worldY;

    return {
      x: this.screenWidth / 2 + relX * this.zoom,
      y: this.screenHeight / 2 + relY * this.zoom,
    };
  }

  public screenToWorld(screenX: number, screenY: number): WorldPoint {
    const relX = screenX - this.screenWidth / 2;
    const relY = screenY - this.screenHeight / 2;

    return {
      x: this.worldX + relX / this.zoom,
      y: this.worldY + relY / this.zoom,
    };
  }
}
